import React from 'react'
import { ViewportPortal, useStore } from '@xyflow/react'
import type { Measure } from './utils/canvasAlignment'
import './MeasureLines.scss'

/** Strichstärke in Bildschirmpixeln; gegen den Zoom gerechnet. */
const LINE_THICKNESS = 2

/**
 * Abstand der Klammer von der Kante, in Bildschirmpixeln.
 *
 * 16 und nicht 8: auf der Kante sitzen schon die Eckpunkte. Die messen 12px
 * und stehen mittig, also 6px über — eine Klammer bei 8px liefe mitten
 * hindurch.
 */
const LINE_OFFSET = 16

/** Länge der beiden Endstriche, in Bildschirmpixeln. */
const CAP_LENGTH = 8

export interface MeasureLinesProps {
  /** Die Klammern; leer, solange keine Größe übereinstimmt. */
  measures: Measure[]
}

/**
 * Die Maßlinien beim Ziehen an einer Ecke: zwei gleich lange Klammern, eine
 * an der gezogenen Notiz und eine an der, deren Größe sie gerade trifft.
 *
 * Warum eine Klammer und nicht etwa ein Rahmen um die Partner-Notiz: Ein
 * Rahmen sagt „diese Notiz ist beteiligt", aber nicht, WORAN. Die Klammer
 * zeigt die Größe selbst — und genau darum geht es beim Ziehen an der Ecke.
 *
 * Gerendert im `ViewportPortal`, wie die Hilfslinien: dort gelten
 * Canvas-Koordinaten, und die Klammern werden mitverschoben und -gezoomt.
 *
 * @example
 * <MeasureLines measures={measures} />
 */
export const MeasureLines: React.FC<MeasureLinesProps> = ({ measures }) => {
  // Strichstärke, Abstand und Endstriche werden gegen den Zoom gerechnet, damit
  // die Klammer bei jeder Zoomstufe gleich aussieht.
  const zoom = useStore(state => state.transform[2])

  if (measures.length === 0) return null

  const thickness = LINE_THICKNESS / zoom
  const cap = CAP_LENGTH / zoom
  // Eine Lücken-Klammer sitzt genau auf `at` — dort misst sie den Raum
  // ZWISCHEN zwei Notizen, und `at` liegt schon mittig im Band. Der Versatz
  // gilt nur für Klammern an einer Kante (`'size'`, die Vorgabe).
  const offsetOf = (measure: Measure) => (measure.kind === 'gap' ? 0 : LINE_OFFSET / zoom)
  // Die Klasse sagt, welche Art von Messung hier steht — die FARBE hängt
  // nicht mehr daran, beide Arten sind violett (siehe `MeasureLines.scss`).
  // Gebraucht wird sie weiterhin für den Versatz oben und im DOM als Auskunft.
  const classOf = (measure: Measure, axis: 'x' | 'y') =>
    `canvas-measure-line canvas-measure-line--${axis}` +
    (measure.kind === 'gap' ? ' canvas-measure-line--gap' : '')
  // Die Endstriche sind ein reines Aussehen-Detail und stehen deshalb in der
  // SCSS; ihre zoomabhängigen Maße kommen als Custom Properties herüber.
  const vars = { '--measure-thickness': `${thickness}px`, '--measure-cap': `${cap}px` }

  return (
    <ViewportPortal>
      {measures.map(measure =>
        measure.axis === 'x' ? (
          <div
            key={`x${measure.from}-${measure.to}-${measure.at}`}
            className={classOf(measure, 'x')}
            style={{
              left: measure.from,
              top: measure.at + offsetOf(measure),
              width: measure.to - measure.from,
              height: thickness,
              ...vars,
            } as React.CSSProperties}
          />
        ) : (
          <div
            key={`y${measure.from}-${measure.to}-${measure.at}`}
            className={classOf(measure, 'y')}
            style={{
              left: measure.at + offsetOf(measure),
              top: measure.from,
              width: thickness,
              height: measure.to - measure.from,
              ...vars,
            } as React.CSSProperties}
          />
        )
      )}
    </ViewportPortal>
  )
}
